import { useCallback, useMemo, useState } from 'react'
import type { FilterSpec, ListViewDefinition, SortSpec } from './types'

/**
 * Состояние настроенного списка: колонки, фильтры, сортировка, группировка (ТЗ §5.1).
 *
 * Держится отдельно от компонента таблицы, потому что то же самое состояние сохраняется
 * представлением, уходит в выгрузку и в запрос страницы. Спрятанное внутри таблицы, оно
 * потребовало бы протаскивать его наружу тремя разными способами.
 */

/** Ключ страницы: смена любой его части обесценивает накопленные курсоры. */
export interface ListState extends ListViewDefinition {
  columns: string[]
  filters: FilterSpec[]
  sort: SortSpec[]
  groupBy: string | null
  pageSize: number
}

export interface ListStateApi {
  state: ListState
  /** Определение в том виде, в каком оно уходит на сервер и сохраняется. */
  definition: ListViewDefinition
  setColumns: (columns: string[]) => void
  toggleColumn: (column: string) => void
  setFilters: (filters: FilterSpec[]) => void
  addFilter: (filter: FilterSpec) => void
  removeFilter: (index: number) => void
  /**
   * Переключает сортировку по колонке: нет → по возрастанию → по убыванию → нет.
   */
  toggleSort: (column: string) => void
  setGroupBy: (column: string | null) => void
  setPageSize: (pageSize: number) => void
  /** Применяет сохранённое представление целиком. */
  apply: (definition: ListViewDefinition) => void
}

/**
 * Строк на странице по умолчанию.
 *
 * Пятьдесят, а не сто: сто строк — это лента в четыре тысячи пикселей, у которой не видно
 * ни начала, ни конца, а панель страниц внизу оказывается за пределами экрана. Сто и
 * двадцать пять — в выборе размера под таблицей.
 */
export const DEFAULT_PAGE_SIZE = 50

export function useListState(initial?: Partial<ListState>): ListStateApi {
  const [state, setState] = useState<ListState>({
    columns: initial?.columns ?? [],
    filters: initial?.filters ?? [],
    sort: initial?.sort ?? [],
    groupBy: initial?.groupBy ?? null,
    pageSize: initial?.pageSize ?? DEFAULT_PAGE_SIZE,
  })

  const setColumns = useCallback((columns: string[]) => {
    setState((previous) => ({ ...previous, columns }))
  }, [])

  const toggleColumn = useCallback((column: string) => {
    setState((previous) => {
      const shown = previous.columns.includes(column)
      // Последнюю колонку убрать нельзя: список без колонок — это пустой экран, который
      // выглядит как поломка, а вернуть его нечем.
      if (shown && previous.columns.length === 1) return previous
      return {
        ...previous,
        columns: shown
          ? previous.columns.filter((name) => name !== column)
          : [...previous.columns, column],
      }
    })
  }, [])

  const setFilters = useCallback((filters: FilterSpec[]) => {
    setState((previous) => ({ ...previous, filters }))
  }, [])

  const addFilter = useCallback((filter: FilterSpec) => {
    setState((previous) => ({ ...previous, filters: [...previous.filters, filter] }))
  }, [])

  const removeFilter = useCallback((index: number) => {
    setState((previous) => ({
      ...previous,
      filters: previous.filters.filter((_, i) => i !== index),
    }))
  }, [])

  const toggleSort = useCallback((column: string) => {
    setState((previous) => {
      const current = previous.sort[0]
      if (!current || current.column !== column) {
        return { ...previous, sort: [{ column, descending: false }] }
      }
      if (!current.descending) {
        return { ...previous, sort: [{ column, descending: true }] }
      }
      return { ...previous, sort: [] }
    })
  }, [])

  const setGroupBy = useCallback((column: string | null) => {
    setState((previous) => ({ ...previous, groupBy: column }))
  }, [])

  const setPageSize = useCallback((pageSize: number) => {
    setState((previous) => ({ ...previous, pageSize }))
  }, [])

  const apply = useCallback((definition: ListViewDefinition) => {
    setState({
      columns: definition.columns ?? [],
      filters: definition.filters ?? [],
      sort: definition.sort ?? [],
      groupBy: definition.groupBy ?? null,
      pageSize: definition.pageSize ?? DEFAULT_PAGE_SIZE,
    })
  }, [])

  // Отдельный объект, а не сам state: groupBy как `null` сервер поймёт, а как
  // `undefined` Jackson не увидит вовсе — разница проявляется только при снятии
  // группировки, то есть тогда, когда её труднее всего связать с причиной.
  const definition = useMemo<ListViewDefinition>(
    () => ({
      columns: state.columns,
      filters: state.filters,
      sort: state.sort,
      groupBy: state.groupBy,
      pageSize: state.pageSize,
    }),
    [state],
  )

  return {
    state,
    definition,
    setColumns,
    toggleColumn,
    setFilters,
    addFilter,
    removeFilter,
    toggleSort,
    setGroupBy,
    setPageSize,
    apply,
  }
}

/**
 * Ключ кеша страницы.
 *
 * Курсор в него не входит — его добавляет вызывающий к концу ключа: префикс общий у всех
 * страниц одного запроса, и сброс кеша по префиксу снимает их разом. А вот смена фильтра
 * или сортировки
 * обесценивает все накопленные курсоры разом — курсор привязан к той сортировке, для
 * которой выдан, и подставленный к другой молча пропустит строки.
 */
export function listQueryKey(entity: string, definition: ListViewDefinition): unknown[] {
  return [
    'list',
    entity,
    definition.columns,
    definition.filters,
    definition.sort,
    definition.groupBy ?? null,
    definition.pageSize ?? DEFAULT_PAGE_SIZE,
  ]
}
